import React, {Fragment, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Form, Modal, Spinner} from 'react-bootstrap';
import {Formik} from 'formik';
import Dialog from 'react-bootstrap-dialog';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Configuration from '../config';
import {showPlayersModal, hidePlayersModal, searchPlayers, cleanPlayersReceived, showEditPlayersModal, hideEditPlayersModal} from '../actions/player';
import PlayerBookingEditModal from './player-booking-edit-players'
import defaultAvatar from '../images/Replayer_default_Avatar.png'
import binIcon from '../images/bin_icon.svg';
import { showMessage } from '../actions/messages';
import {getDiscountedBookingPrice} from '../util';
import {clearBookingTopupResume, getBookingTopupResume} from '../util/booking-topup-resume';
import {FaCalendarAlt, FaCheckCircle, FaClock, FaEuroSign, FaMapMarkerAlt, FaQuestionCircle, FaUsers} from 'react-icons/fa';
import BookingGuideModal from './booking-guide-modal';
import {BookingPlayerSearch} from './booking-player-search';
import {getDedicatedFieldCostQuote} from '../api';

const sportLabelValue = sport => {
  if (!sport) return '';
  if (typeof sport === 'string' || typeof sport === 'number') return String(sport);
  if (typeof sport === 'object') return sport.name || sport.label || sport.value || sport.sport || sport.type || '';
  return String(sport);
};

export const supportsSinglesDoubles = slot => Boolean(
  slot && Number(slot.presetPlaces) === 4 && Number(slot.minimumPlayers) === 2
);

export const requiredPlayersForMatchFormat = (slot, matchFormat) => {
  if (!supportsSinglesDoubles(slot)) return Number(slot && slot.minimumPlayers) || 1;
  return matchFormat === 'doubles' ? 4 : 2;
};

export const matchFormatForParticipantCount = (slot, participantCount) => {
  if (!supportsSinglesDoubles(slot)) return null;
  return Number(participantCount) === 4 ? 'doubles' : 'singles';
};

export const isMatchFormatParticipantCountValid = (slot, matchFormat, participantCount) => {
  if (!supportsSinglesDoubles(slot)) {
    return Number(participantCount) >= requiredPlayersForMatchFormat(slot, matchFormat);
  }
  return matchFormat === 'doubles'
    ? Number(participantCount) === 4
    : Number(participantCount) === 2 || Number(participantCount) === 3;
};

export const pricingMatchFormatForParticipantCount = (slot, matchFormat, participantCount) => {
  if (!supportsSinglesDoubles(slot)) return matchFormat;
  return Number(participantCount) === 4 || matchFormat === 'doubles' ? 'doubles' : 'singles';
};

export const billablePlayersForInvitation = (slot, matchFormat, participantCount) => {
  const actualPlayers = Math.max(Number(participantCount || 0), 1);
  if (!slot || slot.minimumPlayers === null || slot.minimumPlayers === undefined) return actualPlayers;
  if (supportsSinglesDoubles(slot)) {
    if (actualPlayers === 4 || matchFormat === 'doubles') return 4;
    return Math.max(2, Math.min(actualPlayers, 3));
  }
  return Math.min(Number(slot.presetPlaces || actualPlayers), Math.max(Number(slot.minimumPlayers || 1), actualPlayers));
};

export const applyDoublesDiscount = (price, slot, matchFormat) => {
  if (
    matchFormat !== 'doubles' ||
    !slot ||
    !slot.doublesDiscountEnabled ||
    Number(slot.doublesDiscountPercent || 0) <= 0
  ) return Number(price || 0);
  return Math.round(Number(price || 0) * (100 - Number(slot.doublesDiscountPercent)) / 10) / 10;
};

export const calculateInvitationBookingCosts = ({
  players = [],
  guests = [],
  organizerBasePrice = 0,
  standardBasePrice = 0,
  fixedCosts = 0,
  slot = null,
  matchFormat = null
}) => {
  const peopleCount = players.length + guests.length + 1;
  const billablePeopleCount = billablePlayersForInvitation(slot, matchFormat, peopleCount);
  const unassignedCount = Math.max(billablePeopleCount - peopleCount, 0);
  const pricingMatchFormat = pricingMatchFormatForParticipantCount(slot, matchFormat, billablePeopleCount);
  const fixedShare = Number(fixedCosts || 0) / Math.max(billablePeopleCount, 1);
  const registeredGuests = players.filter(player => player.inviteType === 'guest');
  const guestCount = guests.length + registeredGuests.length;
  const organizerOwn = applyDoublesDiscount(organizerBasePrice, slot, pricingMatchFormat) + fixedShare;
  const guestUnit = applyDoublesDiscount(standardBasePrice, slot, pricingMatchFormat) + fixedShare;
  const registeredGuestsTotal = registeredGuests.reduce((total, player) => {
    const affiliatedShare = player.bookingBasePrice != null
      ? Number(player.bookingBasePrice)
      : standardBasePrice;
    return total + applyDoublesDiscount(affiliatedShare, slot, pricingMatchFormat) + fixedShare;
  }, 0);
  const unassignedTotal = organizerOwn * unassignedCount;
  const invitedGuestsTotal = registeredGuestsTotal + (guestUnit * guests.length);
  const organizerTotal = organizerOwn + unassignedTotal + invitedGuestsTotal;
  const participantsTotal = players
    .filter(player => player.inviteType === 'partecipant')
    .reduce((total, player) => {
      const affiliatedShare = player.bookingBasePrice != null
        ? Number(player.bookingBasePrice)
        : standardBasePrice;
      return total + applyDoublesDiscount(affiliatedShare, slot, pricingMatchFormat) + fixedShare;
    }, 0);

  return {peopleCount, billablePeopleCount, unassignedCount, unassignedTotal, fixedShare, organizerOwn, guestUnit, guestCount, invitedGuestsTotal, organizerTotal, participantsTotal, bookingTotal: organizerTotal + participantsTotal};
};

const UnassignedInvitationActions = ({visible, disabled, onInviteType, onResetPlayer, onCloseConfirmation, onShowPlayers, participantLabel, guestLabel}) => {
  if (!visible) return null;
  const openInvitation = inviteType => {
    onInviteType(inviteType);
    onResetPlayer();
    onCloseConfirmation();
    window.requestAnimationFrame(onShowPlayers);
  };
  return (
    <Fragment>
      <Button type="button" variant="outline-primary" onClick={() => openInvitation('partecipant')} disabled={disabled}>{participantLabel}</Button>
      <Button type="button" variant="outline-primary" onClick={() => openInvitation('guest')} disabled={disabled}>{guestLabel}</Button>
    </Fragment>
  );
};

export const PlayerBooking = ({booking, slot, slots, field, partner, profile, token, save, startGuestTopUp, startBookingTopUp, dedicatedAffiliations, isPlayersModalVisible, showPlayersModal, hidePlayersModal, searchPlayers, clearPlayers, showMessage, discount, showEditPlayersModal, hideEditPlayersModal, isEditPlayersModalVisible, resourceType = 'field', skyworkEmbedded = false}) => {
  const {t, i18n} = useTranslation();
  const resizeArray = (arr, newSize, defaultValue) => {
    if (newSize > arr.length) {
      return [...arr, ...new Array(Math.max(newSize - arr.length, 0)).fill(defaultValue)];
    }
    arr.length = newSize;
    return arr;
  };

  const [selectedPlayer, setSelectedPlayer] = useState({});
  const [insufficientCreditPlayer, setInsufficientCreditPlayer] = useState(null);
  const [showInsufficientCreditChoice, setShowInsufficientCreditChoice] = useState(false);
  const [guestConsent, setGuestConsent] = useState(true);
  const [showTopupConfirm, setShowTopupConfirm] = useState(false);
  const [showOrganizerTopup, setShowOrganizerTopup] = useState(false);
  const [confirmationValues, setConfirmationValues] = useState(null);
  const [confirmationSaving, setConfirmationSaving] = useState(false);
  const [pendingInviteType, setPendingInviteType] = useState(null);
  const [showBookingGuide, setShowBookingGuide] = useState(false);
  const [dedicatedQuote, setDedicatedQuote] = useState(null);
  const [dedicatedQuoteLoading, setDedicatedQuoteLoading] = useState(false);
  const [dedicatedQuoteError, setDedicatedQuoteError] = useState(null);
  const privateBookingDraftRef = useRef(null);
  const confirmationRef = useRef(null);
  const bookingSubmitRef = useRef(null);
  const confirmationSavingRef = useRef(false);
  const [topupResume] = useState(() => getBookingTopupResume());
  const activeSlot = slot || (slots && slots[0]);
  const matchingTopupResume = activeSlot && topupResume && String(topupResume.slotId) === String(activeSlot.slotId)
    ? topupResume
    : null;
  const resumedBookingDraft = matchingTopupResume && matchingTopupResume.bookingDraft
    ? matchingTopupResume.bookingDraft
    : null;
  const formBooking = booking && resumedBookingDraft
    ? {...booking, ...resumedBookingDraft}
    : booking;

  useEffect(() => {
    if (matchingTopupResume) {
      if (matchingTopupResume.resumeMode !== 'booking') showPlayersModal();
      clearBookingTopupResume();
    }
  }, [matchingTopupResume, showPlayersModal]);

  const resumedQuoteSlotIds = slots && slots.length
    ? slots.map(currentSlot => currentSlot.slotId)
    : slot && slot.slotId ? [slot.slotId] : [];
  const resumedQuoteSlotKey = resumedQuoteSlotIds.join(',');
  useEffect(() => {
    privateBookingDraftRef.current = null;
  }, [resumedQuoteSlotKey]);

  useEffect(() => {
    if (
      !formBooking ||
      formBooking.affiliationPricingMode !== 'dedicated_field_cost' ||
      !formBooking.pricingAffiliationId ||
      !formBooking.pricingParticipantCount ||
      !resumedQuoteSlotKey ||
      !token
    ) return undefined;

    let active = true;
    setDedicatedQuoteLoading(true);
    setDedicatedQuoteError(null);
    getDedicatedFieldCostQuote({
      slots: resumedQuoteSlotIds,
      pricingAffiliationId: formBooking.pricingAffiliationId,
      pricingParticipantCount: formBooking.pricingParticipantCount
    }, token).then(res => {
      if (!active) return;
      setDedicatedQuoteLoading(false);
      if (res.status === 200) {
        setDedicatedQuote(res.json);
      } else {
        setDedicatedQuote(null);
        setDedicatedQuoteError(res.json && res.json.error ? res.json.error : 'quote_error');
      }
    });
    return () => {
      active = false;
    };
    // The scalar slot key prevents duplicate quotes when the same draft is reopened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formBooking && formBooking.affiliationPricingMode, formBooking && formBooking.pricingAffiliationId, formBooking && formBooking.pricingParticipantCount, resumedQuoteSlotKey, token]);

  useEffect(() => {
    if (confirmationValues && confirmationRef.current) confirmationRef.current.focus();
  }, [confirmationValues]);

  if (!field || !booking || !profile || !profile.user || !partner || !activeSlot) {
    return (
      <div className="skywork-booking-loading" role="status" aria-live="polite">
        <Spinner animation="border" size="sm"/>
        <span>{t('LOADING_DATA_IN_PROGRESS')}</span>
      </div>
    );
  }
  const inviteRosterCanBeEdited = new Date(activeSlot.startDatetime).getTime() > Date.now();

  let dialog = null;
  const confirm = values => {
    setConfirmationValues(values);
  };

  const checkPrivacy = values => {
    dialog.show({
      title: t('PRIVACY_CONSENT'),
      body: <Fragment>
              <Form.Label>
                <strong>{t('VIDEO_RECORDING_ACTIVE')}</strong>
              </Form.Label>
              <Form.Label>
                {t('GIVE_CONSENT_NOW')}
              </Form.Label>
            </Fragment>,
      actions: [
        Dialog.Action(
          t('I_AGREE'),
          () => confirm(values),
          'btn btn-secondary'
        ),
        Dialog.Action(
          t('NO'),
          null,          
          'btn btn_red_outline_rp'
        )
      ],
      bsSize: 'small',
      onHide: dialog => {
        dialog.hide();
      }
    });
  };

  const slotPrice = () => {
    let price = 0;
    if (slots) {
      for (const currentSlot of slots) {
        price += currentSlot.price;
      }
    } else {
      price = slot.price;
    }

    return getDiscountedBookingPrice(price, discount);
  };

  const slotPriceNoDiscount = () => {
    if (slots) {
      var price = 0;
      for (slot of slots) {
        price += slot.price;
      }
      return price;
    }
    else
    {
      return slot.price;
    }
  }

  const videoRecordingEnabled = resourceType === 'field' && Boolean(field && field.recording);
  const rtvPublishingEnabled = videoRecordingEnabled && Boolean(field && field.rtv);
  const rtvOrAcademy = videoRecordingEnabled ? (slot ? slot.rtvOrAcademy : slots[0].rtvOrAcademy) : null;
  const description = slot ? slot.description : slots[0].description;
  const presetPlaces = slot ? slot.presetPlaces : 1;
  const startSlot = slot ? slot : slots[0];
  const endSlot = slot ? slot : slots[slots.length - 1];
  const recoveryContext = {
    partnerId: partner.uid,
    resourceId: resourceType === 'service' ? startSlot.serviceId : startSlot.fieldId,
    resourceType,
    startDatetime: startSlot.startDatetime
  };
  const dedicatedModeSelected = formBooking.affiliationPricingMode === 'dedicated_field_cost';
  const formOtherPlayers = Array.isArray(formBooking.otherPlayers) ? formBooking.otherPlayers : [];
  const otherPlayers = dedicatedModeSelected
    ? (formOtherPlayers.length ? formOtherPlayers : [''])
    : (formOtherPlayers[0] ? formOtherPlayers : [`${profile.name || ''} ${profile.surname || ''}`.trim()]);
  const otherPlayersPrice = [slotPrice()]
  let rtvChannels = rtvPublishingEnabled
    ? (slots && slots.length > 0
      ? [...new Map(slots.map(s => s.rtvChannels || []).flat().map(item => [item.id, item])).values()]
      : (slot.rtvChannels || []))
    : [];
  
  const rtvChannelAssociations = rtvPublishingEnabled
    ? (slots && slots.length > 0
      ? [...new Map(slots.map(s => s.rtvChannelAssociations || []).flat().map(item => [item.rtvChannelId, item])).values()]
      : (slot.rtvChannelAssociations || []))
    : [];
  rtvChannels.forEach(c => {
    const association = rtvChannelAssociations.find(a => a.rtvChannelId === c.id);
    c.canPublish = Boolean(association && association.canPublish);
  });

  const quoteDedicatedBooking = async (affiliationId, participantCount) => {
    if (!affiliationId || !participantCount) return;
    setDedicatedQuoteLoading(true);
    setDedicatedQuoteError(null);
    const res = await getDedicatedFieldCostQuote({
      slots: slots ? slots.map(current => current.slotId) : [slot.slotId],
      pricingAffiliationId: affiliationId,
      pricingParticipantCount: participantCount
    }, token);
    setDedicatedQuoteLoading(false);
    if (res.status === 200) {
      setDedicatedQuote(res.json);
    } else {
      setDedicatedQuote(null);
      setDedicatedQuoteError(res.json && res.json.error ? res.json.error : 'quote_error');
    }
  };

  const bookingCostSummary = (players, guests, matchFormat = null) => {
    return calculateInvitationBookingCosts({
      players: players || [],
      guests: guests || [],
      organizerBasePrice: slotPrice(),
      standardBasePrice: slotPriceNoDiscount(),
      fixedCosts: (startSlot.costsPrice || 0) / 100,
      slot: startSlot,
      matchFormat
    });
  };

  const closeConfirmation = () => {
    if (confirmationSaving) return;
    setConfirmationValues(null);
    window.requestAnimationFrame(() => {
      if (bookingSubmitRef.current) bookingSubmitRef.current.focus();
    });
  };

  const confirmBooking = async () => {
    if (!confirmationValues || confirmationSavingRef.current) return;
    if (confirmationValues.affiliationPricingMode === 'dedicated_field_cost') {
      if (!dedicatedQuote) return;
      if (Number(dedicatedQuote.shortfall || 0) > 0) {
        startBookingTopUp(
          dedicatedQuote.shortfall,
          startSlot.slotId,
          confirmationValues,
          recoveryContext
        );
        return;
      }
    }
    const confirmationCredit = bookingCostSummary(
      confirmationValues.invitedPlayers || [],
      confirmationValues.invitedGuests || [],
      confirmationValues.matchFormat
    );
    if (
      confirmationValues.affiliationPricingMode !== 'dedicated_field_cost' &&
      field.payment &&
      confirmationCredit.organizerTotal > Number(profile.user.wallet || 0)
    ) {
      startBookingTopUp(
        confirmationCredit.organizerTotal - Number(profile.user.wallet || 0),
        startSlot.slotId,
        confirmationValues,
        recoveryContext
      );
      return;
    }
    confirmationSavingRef.current = true;
    setConfirmationSaving(true);
    try {
      const saved = await save(confirmationValues);
      if (saved) setConfirmationValues(null);
    } finally {
      confirmationSavingRef.current = false;
      setConfirmationSaving(false);
    }
  };

  const renderPlayer = (players, guests, setFieldValue, matchFormat = null) => {
    const costs = bookingCostSummary(players, guests, matchFormat);
    const pricingMatchFormat = pricingMatchFormatForParticipantCount(
      startSlot,
      matchFormat,
      players.length + guests.length + 1
    );
    const requiredPlayers = requiredPlayersForMatchFormat(startSlot, matchFormat);
    const removeInvitedPlayer = index => {
      const nextPlayers = players.filter((_, currentIndex) => currentIndex !== index);
      const nextCount = nextPlayers.length + guests.length + 1;
      const nextMatchFormat = supportsSinglesDoubles(startSlot) && nextCount === 4 ? 'doubles' : matchFormat;
      setFieldValue('invitedPlayers', nextPlayers);
      setFieldValue('playersNumber', billablePlayersForInvitation(startSlot, nextMatchFormat, nextCount));
      if (supportsSinglesDoubles(startSlot)) setFieldValue('matchFormat', nextMatchFormat);
    };

    const removeInvitedGuest = index => {
      const nextGuests = guests.filter((_, currentIndex) => currentIndex !== index);
      const nextCount = players.length + nextGuests.length + 1;
      const nextMatchFormat = supportsSinglesDoubles(startSlot) && nextCount === 4 ? 'doubles' : matchFormat;
      setFieldValue('invitedGuests', nextGuests);
      setFieldValue('playersNumber', billablePlayersForInvitation(startSlot, nextMatchFormat, nextCount));
      if (supportsSinglesDoubles(startSlot)) setFieldValue('matchFormat', nextMatchFormat);
    };

    return(
      <div className={skyworkEmbedded ? 'skywork-booking-participants' : ''}>
      <ul className="list-group list-group-light mb-4">
        <li className="list-group-item d-flex justify-content-between align-items-center skywork-booking-participant">
          <div className="d-flex align-items-center">
            {profile.image && profile.image[0] ?
            <img
              className="img-fluid rounded-circle"
              src={`${Configuration.apiEndPoint}/images/${profile.uid}?load=${profile.image[0]}`}
              alt={t('PROFILE_IMAGE')}
              style={{width: 45, height: 45, paddingLeft:0}}
            />
            :
            <img
              className="img-fluid rounded-circle"
              src={defaultAvatar}
              alt={t('PROFILE_IMAGE')}
              style={{width: 45, height: 45, paddingLeft:0}}
            />
            }
            <div className="ms-3">
              <p className="fw-bold mb-1" style={{marginLeft: 10}}>{profile.name + " " + profile.surname}</p>
              <p className="fw-bold mb-1" style={{marginLeft: 10}}>{profile.nickname + ' - ' + t('REFERENT')}</p>
              <p className="text-muted mb-0 skywork-player-cost" style={{marginLeft: 10}}>{t('SKYWORK_COST_BREAKDOWN', {share: applyDoublesDiscount(slotPrice(), startSlot, pricingMatchFormat).toFixed(2), fixed: costs.fixedShare.toFixed(2), total: costs.organizerOwn.toFixed(2)})}</p>
            </div>
          </div>
        </li>
        {
          players.map( (p, index) =>
            <li key={p.uid} className="list-group-item d-flex justify-content-between align-items-center skywork-booking-participant">
              <div className="d-flex align-items-center">
                {p.image && p.image[0] ?
                <img
                  className="img-fluid rounded-circle"
                  src={`${Configuration.apiEndPoint}/images/${p.uid}?load=${p.image[0]}`}
                  alt={t('PROFILE_IMAGE')}
                  style={{width: 45, height: 45, paddingLeft:0}}
                />
                :
                <img
                  className="img-fluid rounded-circle"
                  src={defaultAvatar}
                  alt={t('PROFILE_IMAGE')}
                  style={{width: 45, height: 45, paddingLeft:0}}
                />
                }
                <div className="ms-3">
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{p.name + " " + p.surname}</p>
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{p.nickname + ' - ' +(p.inviteType === 'partecipant' ? t('PARTECIPANT') : t('GUEST'))}</p>
                  {p.topupInvitation ? <span className="skywork-topup-pending-badge" style={{marginLeft: 10}}>{t('SKYWORK_TOPUP_PENDING_BADGE')}</span> : null}
                  {skyworkEmbedded ? <p className="text-muted mb-0 skywork-player-cost" style={{marginLeft: 10}}>{p.inviteType === 'partecipant'
                    ? t('SKYWORK_PARTICIPANT_COST', {total: (applyDoublesDiscount(p.bookingBasePrice != null ? Number(p.bookingBasePrice) : slotPriceNoDiscount(), startSlot, pricingMatchFormat) + costs.fixedShare).toFixed(2)})
                    : t('SKYWORK_GUEST_COST_PAID_BY_YOU', {share: applyDoublesDiscount(p.bookingBasePrice != null ? Number(p.bookingBasePrice) : slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2), fixed: costs.fixedShare.toFixed(2), total: (applyDoublesDiscount(p.bookingBasePrice != null ? Number(p.bookingBasePrice) : slotPriceNoDiscount(), startSlot, pricingMatchFormat) + costs.fixedShare).toFixed(2)})}</p> : <p className="text-muted mb-0" style={{marginLeft: 10}}>
                    {index + 2 <= requiredPlayers ? p.inviteType === 'partecipant' ?
                    t('GROUP_BOOKING_OTHER_PLAYER_MANDATORY',{index: index+2}) : (t('GROUP_BOOKING_OTHER_PLAYER_MANDATORY_WITH_PRICE',{index: index+2, price: applyDoublesDiscount(p.bookingBasePrice != null ? Number(p.bookingBasePrice) : slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2)}) + (startSlot.costsPrice && startSlot.costsPrice > 0 ? t('SLOT_COST_PRICE', {price: (startSlot.costsPrice/100/(players.length + guests.length + 1)).toFixed(2)}) + ` (${startSlot.costs.map(c => c.name).join(', ')})` : ""))
                    : p.inviteType === 'partecipant' ? t('GROUP_BOOKING_OTHER_PLAYER_OPTIONAL',{index: index+2}) : 
                    (t('GROUP_BOOKING_OTHER_PLAYER_OPTIONAL_WITH_PRICE',{index: index+2, price: applyDoublesDiscount(p.bookingBasePrice != null ? Number(p.bookingBasePrice) : slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2)}) + (startSlot.costsPrice && startSlot.costsPrice > 0 ? t('SLOT_COST_PRICE', {price: (startSlot.costsPrice/100/(players.length + guests.length + 1)).toFixed(2)}) + ` (${startSlot.costs.map(c => c.name).join(', ')})` : ""))}
                  </p>}
                </div>
              </div>
              <span className="card_video_archive_list_icons_item"><button type="button" title={t('SKYWORK_REMOVE_INVITED')} aria-label={t('SKYWORK_REMOVE_INVITED')} onClick={() => removeInvitedPlayer(index)}><img src={binIcon} alt=""/></button></span>
            </li>
          )
        }
        {
          guests.map( (g, index) =>
            <li key={`${g.email || g.phoneNumber || g.surname}_${index}`} className="list-group-item d-flex justify-content-between align-items-center skywork-booking-participant">
              <div className="d-flex align-items-center">
                <img
                  className="img-fluid rounded-circle"
                  src={defaultAvatar}
                  alt={t('PROFILE_IMAGE')}
                  style={{width: 45, height: 45, paddingLeft:0}}
                />
                <div className="ms-3">
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{[g.name, g.surname].filter(Boolean).join(" ")}</p>
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{g.email || "No e-mail - " + t('GUEST')}</p>
                  {skyworkEmbedded ? <p className="text-muted mb-0 skywork-player-cost" style={{marginLeft: 10}}>{t('SKYWORK_GUEST_COST_PAID_BY_YOU', {share: applyDoublesDiscount(slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2), fixed: costs.fixedShare.toFixed(2), total: costs.guestUnit.toFixed(2)})}</p> : <p className="text-muted mb-0" style={{marginLeft: 10}}>
                    {index + 2 + players.length <= requiredPlayers ?
                    (t('GROUP_BOOKING_OTHER_PLAYER_MANDATORY_WITH_PRICE',{index: index+2+players.length, price: applyDoublesDiscount(slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2)}) + (startSlot.costsPrice && startSlot.costsPrice > 0 ? t('SLOT_COST_PRICE', {price: (startSlot.costsPrice/100/(players.length + guests.length + 1)).toFixed(2)}) + ` (${startSlot.costs.map(c => c.name).join(', ')})` : ""))
                    : (t('GROUP_BOOKING_OTHER_PLAYER_OPTIONAL_WITH_PRICE',{index: index+2+players.length, price: applyDoublesDiscount(slotPriceNoDiscount(), startSlot, pricingMatchFormat).toFixed(2)}) + (startSlot.costsPrice && startSlot.costsPrice > 0 ? t('SLOT_COST_PRICE', {price: (startSlot.costsPrice/100/(players.length + guests.length + 1)).toFixed(2)}) + ` (${startSlot.costs.map(c => c.name).join(', ')})` : ""))}
                  </p>}
                </div>
              </div>
              <span className="card_video_archive_list_icons_item"><button type="button" title={t('SKYWORK_REMOVE_INVITED')} aria-label={t('SKYWORK_REMOVE_INVITED')} onClick={() => removeInvitedGuest(index)}><img src={binIcon} alt=""/></button></span>
            </li>
          )
        }
      </ul>
      </div>
    )
  }

  const renderPlayerAfterBooking = (players) => {
    return(
      <div className="skywork-booking-participants skywork-booking-participants--confirmed">
      <ul className="list-group list-group-light mb-4">
        {
          players.map( p =>
            <li key={`${p.uid}_${p.partnerBookingEmail}`} className="list-group-item d-flex justify-content-between align-items-center skywork-booking-participant">
              <div className="d-flex align-items-center">
              {p.image && p.image[0] ?
                <img
                  className="img-fluid rounded-circle"
                  src={`${Configuration.apiEndPoint}/images/${p.uid}?load=${p.image[0]}`}
                  alt={t('PROFILE_IMAGE')}
                  style={{width: 45, height: 45, paddingLeft:0}}
                />
                :
                <img
                  className="img-fluid rounded-circle"
                  src={defaultAvatar}
                  alt={t('PROFILE_IMAGE')}
                  style={{width: 45, height: 45, paddingLeft:0}}
                />
                }
                <div className="ms-3">
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{p.partnerBookingName + " " + p.partnerBookingSurname}</p>
                  <p className="fw-bold mb-1" style={{marginLeft: 10}}>{p.partnerBookingEmail + ' - ' +(p.inviteType === 0 ? t('REFERENT') : p.inviteType === 1 ? t('PARTECIPANT') : t('GUEST'))}</p>
                </div>
              </div>
            </li>
          )
        }
      </ul>
      </div>
    )
  }

  const renderPlayerSearch = (player, affiliatedFilter = false) => {
    const affiliationLabel = player.affiliationNames && player.affiliationNames.length
      ? player.affiliationNames.join(', ')
      : (affiliatedFilter ? partner.name : null);
    return(
      <div key={player.uid}>
        <ul class="list-group list-group-light mb-4">
          <li class="list-group-item d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              {player && player.image && player.image[0] ?
              <img
                className="img-fluid"
                src={`${Configuration.apiEndPoint}/images/${player ? player.uid : profile.uid}?load=${player ? player.image[0] : profile.image[0]}`}
                alt={t('PROFILE_IMAGE')}
                style={{width: 45, height: 45, paddingLeft:0}}
                class="rounded-circle"
              />
              :
              <img
                className="img-fluid"
                src={defaultAvatar}
                alt={t('PROFILE_IMAGE')}
                style={{width: 45, height: 45, paddingLeft:0}}
                class="rounded-circle"
              />
              }
              <div class="ms-3">
                <p class="fw-bold mb-1" style={{marginLeft: 10}}>{player.name + " " + player.surname}</p>
                <p className="fw-bold mb-1" style={{marginLeft: 10}}>{player.nickname + (player.groupType ? " - " + player.groupType : "")}</p>
                {affiliationLabel ? <p className="skywork-player-affiliation" style={{marginLeft: 10}}>{t('SKYWORK_AFFILIATED_TO', {partner: affiliationLabel})}</p> : null}
              </div>
            </div>
            <Form.Check required name="selectedPlayer" type="radio" onChange={() => setSelectedPlayer(player)}/>
          </li>
        </ul>
      </div>
    )
  }

  const submitBooking = bookingValues => {
    const actualPlayers = bookingValues.invitedGuests.length + bookingValues.invitedPlayers.length + 1;
    const normalizedMatchFormat = supportsSinglesDoubles(startSlot) && actualPlayers === 4
      ? 'doubles'
      : bookingValues.matchFormat;
    const normalizedBookingValues = {
      ...bookingValues,
      matchFormat: normalizedMatchFormat,
      playersNumber: billablePlayersForInvitation(startSlot, normalizedMatchFormat, actualPlayers)
    };

    if (booking.bookingId) {
      save(normalizedBookingValues);
    } else if (!field.recording || startSlot.minimumPlayers != null || profile.privacy1) {
      confirm(normalizedBookingValues);
    } else {
      checkPrivacy(normalizedBookingValues);
    }
  };

  const confirmationCosts = confirmationValues
    ? bookingCostSummary(confirmationValues.invitedPlayers || [], confirmationValues.invitedGuests || [], confirmationValues.matchFormat)
    : null;
  const bookingTypeLabel = confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost'
    ? t('DEDICATED_FIELD_COST_BOOKING')
    : confirmationValues && confirmationValues.matchFormat === 'doubles'
    ? t('OPEN_MATCH_FORMAT_DOUBLE')
    : confirmationValues && confirmationValues.matchFormat === 'singles'
    ? t('OPEN_MATCH_FORMAT_SINGLE')
    : startSlot.minimumPlayers !== null
    ? t('SKYWORK_INVITATION_BOOKING')
    : presetPlaces > 1 ? t('BOOKING_SLOT_PLACES') : t('BOOKING_SLOT_SINGLE');
  const sportLabel = sportLabelValue(field.sport);
  const hasPlannedPrice = slotPriceNoDiscount() > 0 || Number(startSlot.costsPrice || 0) > 0;

  return (
    <Fragment>
      <Formik
        enableReinitialize
        initialValues={Object.assign({}, formBooking, {rtvOrAcademy, description: formBooking.description || description || (presetPlaces === 1 || (presetPlaces > 1 && startSlot.minimumPlayers !== null) ? `${profile.name} ${profile.surname}`: null), presetPlaces, startSlot, endSlot, otherPlayers, otherPlayersPrice, discount, rtvChannels, affiliationPricingMode: formBooking.affiliationPricingMode || null, pricingAffiliationId: formBooking.pricingAffiliationId || (dedicatedAffiliations[0] && dedicatedAffiliations[0].affiliation.affiliationId) || '', pricingParticipantCount: formBooking.pricingParticipantCount || 1, matchFormat: formBooking.matchFormat || (supportsSinglesDoubles(startSlot) ? 'singles' : null), playersNumber: supportsSinglesDoubles(startSlot) && !booking.bookingId ? (formBooking.matchFormat === 'doubles' ? 4 : 2) : formBooking.playersNumber, invitedPlayers: formBooking.invitedPlayers || [], invitedGuests: formBooking.invitedGuests || []})}
        onSubmit={submitBooking}
      >
        {({
          handleSubmit,
          handleChange,
          values,
          setFieldValue
        }) => {
          const participantCount = values.invitedPlayers.length + values.invitedGuests.length + 1;
          const effectiveInviteType = pendingInviteType || values.inviteType;
          const effectiveGuestType = values.guestType || (pendingInviteType === 'guest' ? 'player' : null);
          const effectiveMatchFormat = supportsSinglesDoubles(startSlot) && participantCount === 4
            ? 'doubles'
            : values.matchFormat;
          const targetPlayers = presetPlaces;
          const nextParticipantCount = Math.min(participantCount + 1, presetPlaces);
          const nextInviteMatchFormat = supportsSinglesDoubles(startSlot) && nextParticipantCount === 4
            ? 'doubles'
            : effectiveMatchFormat;
          const nextPricingPlayersCount = billablePlayersForInvitation(startSlot, nextInviteMatchFormat, nextParticipantCount);
          const organizerRequiredForNextGuest = player => {
            const nextPlayers = player
              ? [...values.invitedPlayers, {...player, inviteType: 'guest'}]
              : values.invitedPlayers;
            const nextGuests = player
              ? values.invitedGuests
              : [...values.invitedGuests, {name: '', surname: ''}];
            const nextCount = nextPlayers.length + nextGuests.length + 1;
            const nextFormat = supportsSinglesDoubles(startSlot) && nextCount === 4
              ? 'doubles'
              : effectiveMatchFormat;
            return bookingCostSummary(nextPlayers, nextGuests, nextFormat).organizerTotal;
          };
          const organizerTopupAmount = Math.max(5, Math.ceil(organizerRequiredForNextGuest() - Number(profile.user.wallet || 0)));
          const playerNumberChange = args => {
            const num = parseInt(args.target.value, 10);
            values.otherPlayers = resizeArray(values.otherPlayers, num, '');
            setFieldValue('playersNumber', num);
          };
          
          const addInvitedPlayer = (player, inviteType) => {
            if(inviteType === 'guest' && organizerRequiredForNextGuest(player) > profile.user.wallet){
              setShowOrganizerTopup(true);
              return;
            }
            if(inviteType === 'partecipant' && !player.hasSufficientFunds){
              setInsufficientCreditPlayer(player);
              setShowInsufficientCreditChoice(true);
              return;
            }
            player = {...player, inviteType}
            let alreadyInvited = false;
            values.invitedPlayers.forEach(p => {
              if(p.uid === player.uid) alreadyInvited = true;
            });
            if(!alreadyInvited) {
              const nextPlayers = [...values.invitedPlayers, player];
              const nextCount = nextPlayers.length + values.invitedGuests.length + 1;
              const nextMatchFormat = supportsSinglesDoubles(startSlot) && nextCount === 4
                ? 'doubles'
                : effectiveMatchFormat;
              setFieldValue('invitedPlayers', nextPlayers);
              setFieldValue('playersNumber', billablePlayersForInvitation(startSlot, nextMatchFormat, nextCount));
              if (supportsSinglesDoubles(startSlot)) setFieldValue('matchFormat', nextMatchFormat);
            }
            setSelectedPlayer({});
            values.playerName = "";
            values.playerSurname = "";
            values.playerNickname = "";
            setPendingInviteType(null);
            hidePlayersModal();
            setInsufficientCreditPlayer(null);
          };

          const addInvitedGuest = (guest) => {
            var validEmailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
            if(guest.email && !guest.email.match(validEmailRegex)) {
              showMessage(t('EMAIL'), t('INVALID_EMAIL'));
              return;
            }

            if(guest.email === ""){
              guest.email = null
            }

            if(!guest.name && !guest.surname){
              showMessage(t('MANDATORY_FIELDS'), t('INSERT_GUEST_NAME_SURNAME'));
              return;
            }

            if(skyworkEmbedded && !guestConsent){
              showMessage(t('MANDATORY_FIELDS'), t('SKYWORK_GUEST_CONSENT_REQUIRED'));
              return;
            }

            if(!guest.name){
              guest.name = ""
            }

            if(!guest.surname){
              guest.surname = ""
            }

            if(organizerRequiredForNextGuest() > profile.user.wallet){
              setShowOrganizerTopup(true);
              return;
            }
           
            const nextGuests = [...values.invitedGuests, guest];
            const nextCount = values.invitedPlayers.length + nextGuests.length + 1;
            const nextMatchFormat = supportsSinglesDoubles(startSlot) && nextCount === 4
              ? 'doubles'
              : effectiveMatchFormat;
            setFieldValue('invitedGuests', nextGuests);
            setFieldValue('playersNumber', billablePlayersForInvitation(startSlot, nextMatchFormat, nextCount));
            if (supportsSinglesDoubles(startSlot)) setFieldValue('matchFormat', nextMatchFormat);
            values.friendName = "";
            values.friendSurname = "";
            values.friendEmail = null;
            values.friendPhone = "";
            setPendingInviteType(null);
            hidePlayersModal();
            setGuestConsent(true);
          };

          const costs = bookingCostSummary(values.invitedPlayers, values.invitedGuests, effectiveMatchFormat);
          const payableAmount = values.affiliationPricingMode === 'dedicated_field_cost' && dedicatedQuote
            ? Number(dedicatedQuote.total || 0)
            : costs.organizerTotal;

          return (
            <Form onSubmit={handleSubmit} className={skyworkEmbedded ? 'skywork-booking-form' : ''} hidden={confirmationValues !== null} aria-hidden={confirmationValues !== null}>
              {skyworkEmbedded ? <div className="skywork-context-guide"><Button type="button" variant="link" aria-label={t('HOW_BOOKING_WORKS')} onClick={() => setShowBookingGuide(true)}><FaQuestionCircle/><span>{t('HOW_BOOKING_WORKS')}</span></Button></div> : null}
              {skyworkEmbedded ? (
                <div className="skywork-booking-overview">
                  <div className="skywork-booking-overview-item">
                    <span className="skywork-booking-overview-icon"><FaMapMarkerAlt/></span>
                    <span>
                      <small>{booking.bookingId ? resourceType === 'field' ? t('BOOKED_FIELD') : t('BOOKED_SERVICE') : resourceType === 'field' ? t('BOOKING_FIELD') : t('BOOKING_SERVICE')}</small>
                      <strong>{field.name}{field.sport ? ` · ${field.sport}` : ''}</strong>
                      <em>{partner.name}{partner.city ? ` · ${partner.city}` : ''}</em>
                    </span>
                  </div>
                  <div className="skywork-booking-overview-item">
                    <span className="skywork-booking-overview-icon"><FaCalendarAlt/></span>
                    <span>
                      <small>{t('BOOKING_DAY', {day: new Date(values.startSlot.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})})}</small>
                      <strong>{t('FROM_TO_TIME', {fromTime: new Date(values.startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5), toTime: new Date(values.endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)})}</strong>
                      {slots && slots.length > 1 ? <em>{t('BOOKING_MULTI_SLOT')} ({slots.length})</em> : null}
                    </span>
                  </div>
                </div>
              ) : (
                <Fragment>
                  <Form.Label>{booking.bookingId ? resourceType === 'field' ? t('BOOKED_FIELD') : t('BOOKED_SERVICE') : resourceType === 'field' ? t('BOOKING_FIELD') : t('BOOKING_SERVICE')}: {field.name}, {field.sport || ""}</Form.Label>
                  <Form.Group>
                    <Form.Label>{partner.name}, {partner.city}</Form.Label>
                  </Form.Group>
                  <Form.Label>{t('BOOKING_DAY', {day: new Date(values.startSlot.startDatetime).toLocaleDateString(i18n.language, {weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'})})} {t('FROM_TO_TIME', {fromTime: new Date(values.startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5), toTime: new Date(values.endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)})}</Form.Label>
                  {slots && slots.length > 1 ? <Form.Label>{t('BOOKING_MULTI_SLOT')} ({slots.length})</Form.Label> : null}
                </Fragment>
              )}
              {resourceType === 'field' && !booking.bookingId && dedicatedAffiliations.length > 0 ? (
                <section className="skywork-affiliated-booking-mode">
                  <div className="skywork-booking-section-heading">
                    <strong>{t('HOW_DO_YOU_WANT_TO_BOOK')}</strong>
                  </div>
                  <div className="skywork-affiliated-booking-mode__choices">
                    <Button
                      type="button"
                      className={`skywork-affiliated-booking-mode__choice skywork-affiliated-booking-mode__choice--private${values.affiliationPricingMode !== 'dedicated_field_cost' ? ' is-selected' : ''}`}
                      aria-pressed={values.affiliationPricingMode !== 'dedicated_field_cost'}
                      onClick={() => {
                        const privateDraft = privateBookingDraftRef.current;
                        setFieldValue('affiliationPricingMode', null);
                        setFieldValue('playersNumber', privateDraft ? privateDraft.playersNumber : (formBooking.playersNumber || (presetPlaces > 1 ? Math.max(Number(startSlot.minimumPlayers || 1), 1) : 1)));
                        setFieldValue('otherPlayers', privateDraft ? privateDraft.otherPlayers : otherPlayers);
                        setFieldValue('invitedPlayers', privateDraft ? privateDraft.invitedPlayers : (formBooking.invitedPlayers || []));
                        setFieldValue('invitedGuests', privateDraft ? privateDraft.invitedGuests : (formBooking.invitedGuests || []));
                        setFieldValue('matchFormat', privateDraft ? privateDraft.matchFormat : (formBooking.matchFormat || 'singles'));
                        if (privateDraft) setFieldValue('description', privateDraft.description);
                        setDedicatedQuote(null);
                        setDedicatedQuoteError(null);
                      }}
                    >{t('NORMAL_PRIVATE_BOOKING')}</Button>
                    <Button
                      type="button"
                      className={`skywork-affiliated-booking-mode__choice skywork-affiliated-booking-mode__choice--dedicated${values.affiliationPricingMode === 'dedicated_field_cost' ? ' is-selected' : ''}`}
                      aria-pressed={values.affiliationPricingMode === 'dedicated_field_cost'}
                      onClick={() => {
                        if (values.affiliationPricingMode !== 'dedicated_field_cost') {
                          privateBookingDraftRef.current = {
                            playersNumber: values.playersNumber,
                            otherPlayers: [...(values.otherPlayers || [])],
                            invitedPlayers: [...(values.invitedPlayers || [])],
                            invitedGuests: [...(values.invitedGuests || [])],
                            matchFormat: values.matchFormat,
                            description: values.description
                          };
                        }
                        const affiliationId = values.pricingAffiliationId || dedicatedAffiliations[0].affiliation.affiliationId;
                        setFieldValue('affiliationPricingMode', 'dedicated_field_cost');
                        setFieldValue('pricingAffiliationId', affiliationId);
                        setFieldValue('pricingParticipantCount', 1);
                        setFieldValue('playersNumber', 1);
                        setFieldValue('otherPlayers', ['']);
                        setFieldValue('invitedPlayers', []);
                        setFieldValue('invitedGuests', []);
                        quoteDedicatedBooking(affiliationId, 1);
                      }}
                    >{t('DEDICATED_FIELD_COST_BOOKING')}</Button>
                  </div>
                  <small>{values.affiliationPricingMode === 'dedicated_field_cost' ? t('DEDICATED_FIELD_COST_BOOKING_HELP') : t('NORMAL_PRIVATE_BOOKING_HELP')}</small>
                </section>
              ) : null}
              {resourceType === 'field' && !booking.bookingId && values.affiliationPricingMode !== 'dedicated_field_cost' && supportsSinglesDoubles(startSlot) ? (
                <section className="skywork-match-format" aria-labelledby="booking-match-format-title">
                  <div className="skywork-booking-section-heading">
                    <strong id="booking-match-format-title">{t('OPEN_MATCH_FORMAT')}</strong>
                  </div>
                  <div className="skywork-match-format__choices">
                    <Button
                      type="button"
                      variant={effectiveMatchFormat === 'singles' ? 'primary' : 'outline-primary'}
                      aria-pressed={effectiveMatchFormat === 'singles'}
                      disabled={participantCount === 4}
                      onClick={() => {
                        setFieldValue('matchFormat', 'singles');
                        setFieldValue('playersNumber', billablePlayersForInvitation(startSlot, 'singles', participantCount));
                      }}
                    >{t('OPEN_MATCH_FORMAT_SINGLE')}</Button>
                    <Button
                      type="button"
                      variant={effectiveMatchFormat === 'doubles' ? 'primary' : 'outline-primary'}
                      aria-pressed={effectiveMatchFormat === 'doubles'}
                      onClick={() => {
                        setFieldValue('matchFormat', 'doubles');
                        setFieldValue('playersNumber', 4);
                      }}
                    >{t('OPEN_MATCH_FORMAT_DOUBLE')}{startSlot.doublesDiscountEnabled && Number(startSlot.doublesDiscountPercent || 0) > 0 ? ` · -${Number(startSlot.doublesDiscountPercent)}%` : ''}</Button>
                  </div>
                  <Form.Text className="text-muted">
                    {effectiveMatchFormat === 'doubles'
                      ? startSlot.doublesDiscountEnabled
                        ? t('DOUBLES_DISCOUNT_BOOKING_HELP', {discount: Number(startSlot.doublesDiscountPercent || 0)})
                        : t('DOUBLES_BOOKING_NO_DISCOUNT_HELP')
                      : t('SINGLES_BOOKING_HELP')}
                  </Form.Text>
                </section>
              ) : null}
              {
                values.affiliationPricingMode === 'dedicated_field_cost' ? (
                    <section className="skywork-dedicated-field-booking">
                      <Form.Group>
                        <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                        <Form.Control required name="description" as="textarea" maxLength={255} value={values.description || ''} onChange={handleChange}/>
                      </Form.Group>
                      {dedicatedAffiliations.length > 1 ? (
                        <Form.Group>
                          <Form.Label>{t('MEMBERSHIP')}*</Form.Label>
                          <Form.Control required as="select" name="pricingAffiliationId" value={values.pricingAffiliationId} onChange={event => {
                            const affiliationId = Number(event.target.value);
                            setFieldValue('pricingAffiliationId', affiliationId);
                            quoteDedicatedBooking(affiliationId, Number(values.pricingParticipantCount || 1));
                          }}>
                            {dedicatedAffiliations.map(item => <option key={item.affiliation.affiliationId} value={item.affiliation.affiliationId}>{item.affiliation.name}</option>)}
                          </Form.Control>
                        </Form.Group>
                      ) : null}
                      <Form.Group>
                        <Form.Label>{t('DEDICATED_PARTICIPANT_COUNT')}*</Form.Label>
                        <Form.Control required as="select" name="pricingParticipantCount" value={values.pricingParticipantCount} onChange={event => {
                          const count = Number(event.target.value);
                          const names = [...(values.otherPlayers || [])].slice(0, count);
                          while (names.length < count) names.push('');
                          setFieldValue('pricingParticipantCount', count);
                          setFieldValue('playersNumber', count);
                          setFieldValue('otherPlayers', names);
                          quoteDedicatedBooking(values.pricingAffiliationId, count);
                        }}>
                          {[1, 2, 3, 4].map(count => <option value={count} key={count}>{count}</option>)}
                        </Form.Control>
                      </Form.Group>
                      {(values.otherPlayers || []).map((name, index) => (
                        <Form.Group key={`dedicated_player_${index}`}>
                          <Form.Label>{t('OPTIONAL_PARTICIPANT_NAME', {index: index + 1})}</Form.Label>
                          <Form.Control name={`otherPlayers[${index}]`} type="text" maxLength={50} value={name || ''} onChange={handleChange}/>
                        </Form.Group>
                      ))}
                      <div className="skywork-dedicated-field-quote" aria-live="polite">
                        {dedicatedQuoteLoading ? <span><Spinner animation="border" size="sm"/> {t('CALCULATING_PRICE')}</span> : dedicatedQuote ? (
                          <Fragment>
                            <span><small>{t('DEDICATED_BASE_RATE')}</small><strong>{Number(dedicatedQuote.baseHourlyRate).toFixed(2)} €</strong></span>
                            <span><small>{t('DEDICATED_ADDITIONAL_RATE')}</small><strong>{Number(dedicatedQuote.additionalParticipantsHourlyRate).toFixed(2)} €</strong></span>
                            <span><small>{t('HOURLY_RATE')}</small><strong>{Number(dedicatedQuote.hourlyRate).toFixed(2)} €</strong></span>
                            <span><small>{t('FIELD_PRICE_LABEL')}</small><strong>{Number(dedicatedQuote.fieldTotal).toFixed(2)} €</strong></span>
                            <span><small>{t('FIXED_COSTS')}</small><strong>{Number(dedicatedQuote.fixedCosts).toFixed(2)} €</strong></span>
                            <span className="skywork-dedicated-field-quote__total"><small>{t('TOTAL')}</small><strong>{Number(dedicatedQuote.total).toFixed(2)} €</strong></span>
                            {Number(dedicatedQuote.shortfall || 0) > 0 ? <em>{t('DEDICATED_FIELD_COST_TOPUP_REQUIRED', {amount: Number(dedicatedQuote.shortfall).toFixed(2)})}</em> : null}
                          </Fragment>
                        ) : dedicatedQuoteError ? <em>{t('DEDICATED_FIELD_COST_QUOTE_ERROR')}</em> : null}
                      </div>
                    </section>
                  ) : values.presetPlaces === 1 ? <Fragment>
                    <Form.Group>
                      <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                      <Form.Control required name="description" as="textarea" maxLength={255} placeholder={resourceType === 'field' ? t('ENTER_MATCH_DESCRIPTION'): ''} value={values.description} onChange={handleChange}/>
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>{resourceType === 'field' ? t('BOOKING_PLAYERS_NUMBER') : t('BOOKING_USERS_NUMBER')}*</Form.Label>
                      <Form.Control required as="select" name="playersNumber" value={values.playersNumber || ''} onChange={playerNumberChange}>
                        <option value="">{t('BOOKING_PLAYERS_NUMBER_DESC')}</option>
                        {
                          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(nPlayer => (<option value={nPlayer} key={nPlayer}>{nPlayer}</option>))
                        } 
                      </Form.Control>
                    </Form.Group>
                    {
                      values.otherPlayers.map((others, index) => (
                        <Form.Group key={`player_${index}`}>
                          <Form.Label>{resourceType === 'field' ? t('BOOKING_PLAYERS_NUMBER_INDEX', {index: index + 1}) : t('BOOKING_USERS_NUMBER_INDEX', {index: index + 1})}</Form.Label>
                          <Form.Control name={`otherPlayers[${index}]`} type="text" maxLength={50} value={values.otherPlayers[index] || ''} onChange={handleChange}/>
                        </Form.Group>
                      ))
                    }
                    {
                      resourceType === 'field' ?
                        rtvPublishingEnabled && values.rtvChannels.length > 0 ?
                        <Fragment>
                          <Form.Label>
                            {t('WHERE_PUBLISH')}
                          </Form.Label>
                          {
                            values.rtvChannels.map((c, index) => (
                              <Form.Group>
                                <Form.Check
                                  type="checkbox"
                                  label={`Rtv ${c.name}`}
                                  checked={c.canPublish}
                                  onChange={() => {
                                    values.rtvChannels[index].canPublish = !values.rtvChannels[index].canPublish;
                                    setFieldValue('rtvChannels', values.rtvChannels);                                
                                  }}/>                              
                              </Form.Group>
                            ))
                          }
                          <Form.Label>
                            {t('BOOKING_REPLAYER_TV_DESC')}
                          </Form.Label>
                        </Fragment> :
                          videoRecordingEnabled ? <Form.Group>
                            <Form.Label>
                              {t('BOOKING_VIDEO_STORE')}
                            </Form.Label>
                          </Form.Group> : null
                        : null
                    }
                  </Fragment>
                : 
                <Fragment>
                  {
                    values.presetPlaces > 1 && startSlot.minimumPlayers !== null && !skyworkEmbedded ?
                      <Form.Group>
                        <Form.Label>{t('DESCRIPTION')}*</Form.Label>
                        <Form.Control required name="description" as="textarea" maxLength={255} placeholder={t('DESCRIPTION')} value={values.description} onChange={handleChange}/>
                      </Form.Group>
                    :
                      !skyworkEmbedded ? <Form.Group>
                        <Form.Label>{t('DESCRIPTION')}: {values.description || ''} </Form.Label>
                      </Form.Group> : null
                  }
                  {!skyworkEmbedded && videoRecordingEnabled ? <Form.Group>
                    <Form.Label>{t('BOOKING_REPLAYER_TV_LIST', {list: values.rtvChannels.length > 0 ? Array.from(values.rtvChannels, x => x.name).join(', ') : " No"})}</Form.Label>
                  </Form.Group> : null}
                </Fragment>
              }
              {
                field.payment && startSlot.minimumPlayers === null && values.affiliationPricingMode !== 'dedicated_field_cost' ?
                  <Form.Group>
                    <Form.Label className="font-weight-bold">{(resourceType === 'field' ? t('FIELD_PRICE', {price: slotPrice().toFixed(1)}) : t('SERVICE_PRICE', {price: slotPrice().toFixed(1)})) + (resourceType === 'field' && startSlot.costsPrice > 0 && values.presetPlaces === 1 ? t('SLOT_COST_PRICE', {price: (startSlot.costsPrice/100).toFixed(2)}) + ` (${startSlot.costs.map(c => c.name).join(', ')})` : "")} </Form.Label>
                  </Form.Group>
                : null
              }
              {
                field.unbookableHours && !skyworkEmbedded ?
                <Form.Group>
                  <Form.Label className="font-weight-bold">{
                    new Date() < new Date(values.startSlot.startDatetime).setHours(new Date(values.startSlot.startDatetime).getHours() - field.unbookableHours) ?
                      t('BOOKING_CANCEL_LIMIT', {hours: Math.floor((new Date(values.startSlot.startDatetime).setHours(new Date(values.startSlot.startDatetime).getHours() - field.unbookableHours) - new Date())/(1000 * 60 * 60))})
                      : t('BOOKING_CANCEL_LIMIT_PASSED') }
                  </Form.Label>
                </Form.Group>
                : null
              }
              {
                resourceType === 'field' && values.affiliationPricingMode !== 'dedicated_field_cost' && presetPlaces > 1 && startSlot.minimumPlayers !== null && !booking.bookingId ?
                <Form.Group className={skyworkEmbedded ? 'skywork-booking-invitation' : ''}>
                    {skyworkEmbedded ? (
                      <div className="skywork-booking-section-heading">
                        <strong>{t('SKYWORK_BOOKING_PARTICIPANTS')}</strong>
                        <span>{values.invitedPlayers.length + values.invitedGuests.length + 1} / {targetPlayers}</span>
                      </div>
                    ) : <Form.Label>{supportsSinglesDoubles(startSlot) ? effectiveMatchFormat === 'doubles' ? t('PLAYERS_MIN_EQUALS_MAX', {minmax: 4}) : t('PLAYERS_MIN_MAX', {min: 2, max: 3}) : startSlot.minimumPlayers !== presetPlaces ? t('PLAYERS_MIN_MAX',{min: startSlot.minimumPlayers, max: presetPlaces}) : t('PLAYERS_MIN_EQUALS_MAX',{minmax: presetPlaces})}</Form.Label>}
                    {renderPlayer(values.invitedPlayers, values.invitedGuests, setFieldValue, effectiveMatchFormat)}
                    {values.invitedPlayers.length + values.invitedGuests.length + 1 < targetPlayers ?
                    skyworkEmbedded ? (
                      <div className="skywork-booking-invite-actions">
                        <Button type="button" variant="outline-primary" className="skywork-booking-invite-actions__cta" onClick={() => {
                          setPendingInviteType(null);
                          setFieldValue('inviteType', 'partecipant');
                          setInsufficientCreditPlayer(null);
                          showPlayersModal();
                        }}>{t('SKYWORK_BOOKING_ADD_PARTICIPANT')}<small>{t('SKYWORK_BOOKING_PARTICIPANT_PAYS')}</small></Button>
                        <Button type="button" variant="outline-primary" className="skywork-booking-invite-actions__cta skywork-booking-invite-actions__cta--alt" onClick={() => {
                          setPendingInviteType(null);
                          setFieldValue('inviteType', 'guest');
                          setFieldValue('guestType', 'player');
                          setInsufficientCreditPlayer(null);
                          setSelectedPlayer({});
                          showPlayersModal();
                        }}>{t('SKYWORK_BOOKING_INVITE_GUEST')}<small>{t('SKYWORK_BOOKING_ORGANIZER_PAYS')}</small></Button>
                      </div>
                    ) : <Button variant="secondary" style={{marginBottom: 50}} onClick={showPlayersModal}>{t('INVITE_PLAYER_NOT_REQUIRED', {n: values.invitedGuests.length + values.invitedPlayers.length + 2})}</Button>
                    : null}
                    {skyworkEmbedded ? (
                      <Fragment>
                        <div className="skywork-booking-limits">
                          {supportsSinglesDoubles(startSlot) ? effectiveMatchFormat === 'doubles' ? t('PLAYERS_MIN_EQUALS_MAX', {minmax: 4}) : t('PLAYERS_MIN_MAX', {min: 2, max: 3}) : startSlot.minimumPlayers !== presetPlaces ? t('PLAYERS_MIN_MAX',{min: startSlot.minimumPlayers, max: presetPlaces}) : t('PLAYERS_MIN_EQUALS_MAX',{minmax: presetPlaces})}
                        </div>
                        {(field.payment || hasPlannedPrice) ? <div className="skywork-booking-costs">
                          <strong>{t('SKYWORK_BOOKING_COSTS')}</strong>
                          <div className="skywork-cost-overview">
                            <span><small>{t('SKYWORK_YOUR_SHARE')}</small><b>{applyDoublesDiscount(slotPrice(), startSlot, pricingMatchFormatForParticipantCount(startSlot, effectiveMatchFormat, participantCount)).toFixed(2)} €</b></span>
                            <span><small>{t('SKYWORK_YOUR_FIXED_COSTS')}</small><b>{costs.fixedShare.toFixed(2)} €</b></span>
                            <span><small>{t('SKYWORK_GUESTS_PAID_BY_YOU')}</small><b>{costs.invitedGuestsTotal.toFixed(2)} €</b></span>
                            {costs.unassignedCount > 0 ? <span><small>{t('SKYWORK_UNASSIGNED_SHARES_PAID_BY_YOU', {count: costs.unassignedCount})}</small><b>{costs.unassignedTotal.toFixed(2)} €</b></span> : null}
                            <span className="skywork-cost-total"><small>{field.payment ? t('SKYWORK_YOU_PAY_NOW') : t('SKYWORK_PAY_AT_CLUB_TOTAL')}</small><b>{costs.organizerTotal.toFixed(2)} €</b></span>
                          </div>
                          <div className="skywork-booking-grand-total">{t('SKYWORK_BOOKING_GRAND_TOTAL')} <strong>{costs.bookingTotal.toFixed(2)} €</strong><small>{t('SKYWORK_BOOKING_GRAND_TOTAL_HELP')}</small></div>
                        </div> : null}
                        {!field.payment ? <div className="skywork-booking-offline-payment">{t('MAKING_RESERVATION_TO_PAY')}</div> : null}
                      </Fragment>
                    ) : null}
                </Form.Group>
                : null
              }
              {
                resourceType === 'field' && presetPlaces > 1 && startSlot.minimumPlayers !== null && booking.bookingId && !isEditPlayersModalVisible ?
                <Form.Group>
                  {renderPlayerAfterBooking(booking.users)}
                  {inviteRosterCanBeEdited && booking.users.some(u => u.uid === profile.uid && u.inviteType === 0 ) ?
                    <Button variant="secondary" onClick={showEditPlayersModal}>{t('EDIT_PLAYERS')}</Button> 
                  : null}
                </Form.Group>
                : null
              }
              {
                values.presetPlaces > 1 && booking && booking.bookingId ?
                  null
                : <Fragment>
                    {values.presetPlaces > 1 && rtvPublishingEnabled && values.rtvChannels.length > 0 ? (
                      <Form.Group className="skywork-booking-rtv-options">
                        <Form.Label>{t('BOOKING_REPLAYER_TV')}</Form.Label>
                        {values.rtvChannels.map((channel, index) => (
                          <Form.Check
                            key={channel.id}
                            type="checkbox"
                            label={channel.name}
                            checked={channel.canPublish}
                            onChange={() => {
                              const updatedChannels = values.rtvChannels.map((currentChannel, currentIndex) => currentIndex === index
                                ? {...currentChannel, canPublish: !currentChannel.canPublish}
                                : currentChannel);
                              setFieldValue('rtvChannels', updatedChannels);
                            }}
                          />
                        ))}
                        <Form.Text>{t('BOOKING_REPLAYER_TV_DESC')}</Form.Text>
                      </Form.Group>
                    ) : null}
                    {skyworkEmbedded ? <div className={`skywork-payable-before-booking ${!field.payment && values.affiliationPricingMode !== 'dedicated_field_cost' ? 'skywork-payable-before-booking--offline' : ''}`}>
                      <span>{values.affiliationPricingMode === 'dedicated_field_cost' || field.payment ? t('SKYWORK_AMOUNT_DUE_TO_BOOK') : hasPlannedPrice ? t('SKYWORK_PAY_AT_CLUB_TOTAL') : t('SKYWORK_PAY_AT_CLUB')}</span>
                      {(values.affiliationPricingMode === 'dedicated_field_cost' || field.payment || hasPlannedPrice) ? <strong>{payableAmount.toFixed(2)} €</strong> : null}
                    </div> : null}
                    <Button ref={bookingSubmitRef} type="submit" variant="secondary" style={{marginBottom: 50}}>{t('BOOK')}</Button>
                    {skyworkEmbedded && field.unbookableHours ? (
                      <div className="skywork-booking-cancellation-note skywork-booking-cancellation-note--after-action">
                        {new Date() < new Date(values.startSlot.startDatetime).setHours(new Date(values.startSlot.startDatetime).getHours() - field.unbookableHours)
                          ? t('BOOKING_CANCEL_LIMIT', {hours: Math.floor((new Date(values.startSlot.startDatetime).setHours(new Date(values.startSlot.startDatetime).getHours() - field.unbookableHours) - new Date())/(1000 * 60 * 60))})
                          : t('BOOKING_CANCEL_LIMIT_PASSED')}
                      </div>
                    ) : null}
                  </Fragment>
              }
              {isPlayersModalVisible ?
              <Modal show={isPlayersModalVisible} onHide={() => { setPendingInviteType(null); hidePlayersModal(); }} dialogClassName={skyworkEmbedded ? 'skywork-invite-modal' : ''}>
                <Modal.Header closeButton>
                  <Modal.Title>{skyworkEmbedded
                    ? (effectiveInviteType === 'partecipant' ? t('SKYWORK_PARTICIPANT_MODAL_TITLE') : t('SKYWORK_GUEST_MODAL_TITLE'))
                    : t('BOOKING_PLAYERS_NUMBER_INDEX', {index: (values.invitedGuests || []).length + (values.invitedPlayers || []).length + 2})}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form onSubmit={handleSubmit} className={skyworkEmbedded ? 'skywork-invite-form' : ''}>
                    {skyworkEmbedded ? <div className="skywork-invite-slot-summary">
                      <strong>{field.name} · {field.sport || t('SPORT')}</strong>
                      <span>{new Date(startSlot.startDatetime).toLocaleDateString(i18n.language)} · {new Date(startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}–{new Date(endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}</span>
                      <small>{t('SKYWORK_INVITATION_BOOKING')}</small>
                    </div> : null}
                    {!skyworkEmbedded ? <Fragment>
                      <Form.Label>{t('CHOOSE_INVITE_TYPE')}</Form.Label>
                      <Form.Check required name="inviteType" type="radio" label={t('INVITE_GUEST')} checked={effectiveInviteType === 'guest'} onChange={() => { setPendingInviteType('guest'); setFieldValue('inviteType', 'guest'); }}/>
                      <Form.Text className='text-muted' style={{paddingLeft: 20}}>{t('INVITE_GUEST_INFO')}</Form.Text>
                      <Form.Check required name="inviteType" type="radio" label={t('INVITE_PARTECIPANT')} checked={effectiveInviteType === 'partecipant'} onChange={() => { setPendingInviteType('partecipant'); setFieldValue('inviteType', 'partecipant'); }}/>
                      <Form.Text className='text-muted' style={{paddingLeft: 20, paddingBottom: 30}}>{t('INVITE_PARTECIPANT_INFO')}</Form.Text>
                    </Fragment> : (
                      <div className="skywork-booking-payment-choice">
                        <strong>{effectiveInviteType === 'partecipant' ? t('SKYWORK_PARTICIPANT_PAYMENT_EXPLANATION') : t('SKYWORK_GUEST_PAYMENT_EXPLANATION')}</strong>
                        <span>{effectiveInviteType === 'partecipant' ? t('SKYWORK_PARTICIPANT_PAYMENT_DETAIL') : t('SKYWORK_GUEST_PAYMENT_DETAIL')}</span>
                      </div>
                    )}
                    {effectiveInviteType === 'guest' ?
                    <Fragment>
                      <div className={skyworkEmbedded ? 'skywork-guest-source-choice' : ''}>
                        <Form.Label>{t('CHOOSE_GUEST')}</Form.Label>
                        <Form.Check required name="guestType" type="radio" label={t('BOOKING_SEARCH_REGISTERED_PLAYER')} checked={effectiveGuestType === 'player'} onChange={() => {
                          setSelectedPlayer({});
                          setFieldValue('guestType', 'player');
                        }}/>
                        {!skyworkEmbedded ? <Form.Text className='text-muted' style={{paddingLeft: 20}}>{t('PLAYER_GUEST_INFO')}</Form.Text> : null}
                        <Form.Check required name="guestType" type="radio" label={t('BOOKING_ENTER_UNREGISTERED_GUEST')} checked={effectiveGuestType === 'friend'} onChange={() => {
                          setSelectedPlayer({});
                          clearPlayers();
                          setFieldValue('guestType', 'friend');
                        }}/>
                        {!skyworkEmbedded ? <Form.Text className='text-muted' style={{paddingLeft: 20, paddingBottom: 30}}>{t('FRIEND_GUEST_INFO')}</Form.Text> : null}
                      </div>
                    </Fragment>
                    : null }
                    {effectiveInviteType === 'partecipant' || (effectiveInviteType === 'guest' && effectiveGuestType === 'player') ?
                      <BookingPlayerSearch
                        partner={partner}
                        slotId={startSlot.slotId}
                        playersNumber={nextPricingPlayersCount}
                        matchFormat={nextInviteMatchFormat}
                        searchPlayers={searchPlayers}
                        clearPlayers={clearPlayers}
                        renderPlayer={renderPlayerSearch}
                        selectedPlayer={selectedPlayer}
                        onAddPlayer={() => addInvitedPlayer(selectedPlayer, effectiveInviteType)}
                        onGuestFallback={({name, surname}) => {
                          setSelectedPlayer({});
                          setPendingInviteType('guest');
                          setFieldValue('inviteType', 'guest');
                          setFieldValue('guestType', 'friend');
                          setFieldValue('friendName', name);
                          setFieldValue('friendSurname', surname);
                        }}
                        showNickname={!skyworkEmbedded}
                        skywork={skyworkEmbedded}
                      />
                    : effectiveGuestType === 'friend' ?
                    <Fragment>
                      <p className="skywork-search-help">{t('SKYWORK_GUEST_IDENTITY_HELP')}</p>
                      <Form.Group className={skyworkEmbedded ? 'skywork-guest-name-fields' : ''} style={!skyworkEmbedded ? {display: 'flex', flexDirection: 'row'} : null}>
                        <Form.Control name="friendName" placeholder={t('NAME')} style={!skyworkEmbedded ? {marginRight: 20} : null} type="text" maxLength="25" minLength="2" value={values.friendName || ""} onChange={handleChange}/>
                        <Form.Control name="friendSurname" placeholder={t('SURNAME')} type="text" maxLength="25" minLength="2" value={values.friendSurname || ""} onChange={handleChange}/>
                      </Form.Group>
                      <Form.Group className={skyworkEmbedded ? 'skywork-guest-contact-fields' : ''} style={!skyworkEmbedded ? {display: 'flex', flexDirection: 'row'} : null}>
                        <Form.Control name="friendEmail" placeholder={t('EMAIL')} style={!skyworkEmbedded ? {marginRight: 20} : null} type="email" maxLength="80" value={values.friendEmail || ""} onChange={handleChange}/>
                        <Form.Control name="friendPhone" className='no-arrow' placeholder={t('PHONE')} type="number" maxLength="10" minLength="10" value={values.friendPhone || ""} onChange={handleChange}/>
                      </Form.Group>
                      {skyworkEmbedded ? <Fragment>
                        <div className="skywork-who-pays"><strong>{t('SKYWORK_WHO_PAYS')}</strong><div>◉ {t('SKYWORK_ORGANIZER_PAYS_GUEST')}</div></div>
                        <div className="skywork-guest-cost-summary"><span>{t('SKYWORK_GUEST_SHARE')} <b>{slotPriceNoDiscount().toFixed(2)} €</b></span><span>{t('SKYWORK_BOOKING_TOTAL_ADDITIONAL')} <b>{(slotPriceNoDiscount() + ((startSlot.costsPrice || 0) / 100 / Math.max(startSlot.minimumPlayers || 1, 1))).toFixed(2)} €</b></span></div>
                        <p className="skywork-conversion-note">{t('SKYWORK_GUEST_CAN_CONVERT')}</p>
                        <Form.Check type="checkbox" checked={guestConsent} onChange={event => setGuestConsent(event.target.checked)} label={t('SKYWORK_GUEST_CONSENT')}/>
                      </Fragment> : null}
                      <Button type="button" className={skyworkEmbedded ? 'skywork-primary-action' : ''} style={!skyworkEmbedded ? {maxWidth: 100} : null} variant="secondary" onClick={() => addInvitedGuest({email: values.friendEmail, name: values.friendName, surname: values.friendSurname, phoneNumber: values.friendPhone})}>{skyworkEmbedded ? t('SKYWORK_ADD_GUEST') : t('SELECT')}</Button>
                    </Fragment>
                    : null}
                  </Form>
                </Modal.Body>
                <Modal.Footer className={skyworkEmbedded ? 'skywork-invite-footer' : ''}>
                  <Button variant="primary" onClick={hidePlayersModal}>
                    {t('CLOSE_BUTTON')}
                  </Button>
                </Modal.Footer>
              </Modal>
              : null}
              <Modal
                show={showInsufficientCreditChoice}
                onHide={() => setShowInsufficientCreditChoice(false)}
                dialogClassName="skywork-invite-modal skywork-credit-choice-modal"
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>{t('SKYWORK_CREDIT_INSUFFICIENT_TITLE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="skywork-invite-slot-summary">
                    <strong>{field.name} · {field.sport || t('SPORT')}</strong>
                    <span>{new Date(startSlot.startDatetime).toLocaleDateString(i18n.language)} · {new Date(startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}–{new Date(endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}</span>
                  </div>
                  <p className="skywork-credit-choice-message">{t('SKYWORK_CREDIT_INSUFFICIENT_BODY', {name: insufficientCreditPlayer ? insufficientCreditPlayer.name : ''})}</p>
                  <p className="skywork-topup-safety-note">{t('SKYWORK_CREDIT_CHOICE_DEADLINE')}</p>
                </Modal.Body>
                <Modal.Footer className="skywork-invite-footer skywork-credit-choice-actions">
                  <Button type="button" className="skywork-primary-action" onClick={() => {
                    setShowInsufficientCreditChoice(false);
                    setShowTopupConfirm(true);
                  }}>{t('SKYWORK_SEND_TOPUP_INVITE')}</Button>
                  <Button type="button" variant="outline-primary" onClick={() => {
                    const player = insufficientCreditPlayer;
                    setShowInsufficientCreditChoice(false);
                    addInvitedPlayer(player, 'guest');
                  }}>{t('SKYWORK_INVITE_AS_GUEST')}</Button>
                  <Button type="button" variant="outline-primary" onClick={() => setShowInsufficientCreditChoice(false)}>{t('CLOSE_BUTTON')}</Button>
                </Modal.Footer>
              </Modal>
              <Modal show={showTopupConfirm} onHide={() => setShowTopupConfirm(false)} dialogClassName="skywork-invite-modal skywork-topup-confirm-modal">
                <Modal.Header closeButton>
                  <Modal.Title>{t('SKYWORK_TOPUP_CONFIRM_TITLE')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="skywork-invite-slot-summary">
                    <strong>{field.name} · {field.sport || t('SPORT')}</strong>
                    <span>{new Date(startSlot.startDatetime).toLocaleDateString(i18n.language)} · {new Date(startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}–{new Date(endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}</span>
                  </div>
                  <p>{t('SKYWORK_TOPUP_CONFIRM_BODY', {name: insufficientCreditPlayer ? insufficientCreditPlayer.name : ''})}</p>
                  <ul className="skywork-topup-steps">
                    <li>{t('SKYWORK_TOPUP_CONFIRM_EMAIL')}</li>
                    <li>{t('SKYWORK_TOPUP_CONFIRM_ACCEPT')}</li>
                    <li>{t('SKYWORK_TOPUP_CONFIRM_TRANSFER')}</li>
                    <li>{t('SKYWORK_TOPUP_CONFIRM_NOTIFY')}</li>
                  </ul>
                  <p className="skywork-topup-safety-note">{t('SKYWORK_TOPUP_CONFIRM_PENDING')}</p>
                </Modal.Body>
                <Modal.Footer className="skywork-invite-footer">
                  <Button type="button" variant="outline-primary" onClick={() => setShowTopupConfirm(false)}>{t('CANCEL')}</Button>
                  <Button type="button" className="skywork-primary-action" onClick={() => {
                    const player = insufficientCreditPlayer;
                    if (!player) return;
                    if (organizerRequiredForNextGuest() > profile.user.wallet) {
                      setShowTopupConfirm(false);
                      setShowOrganizerTopup(true);
                      return;
                    }
                    const alreadyInvited = values.invitedPlayers.some(item => item.uid === player.uid);
                    if (!alreadyInvited) {
                      setFieldValue('invitedPlayers', [
                        ...values.invitedPlayers,
                        {...player, inviteType: 'guest', topupInvitation: true}
                      ]);
                    }
                    setShowTopupConfirm(false);
                    setShowInsufficientCreditChoice(false);
                    setInsufficientCreditPlayer(null);
                    setSelectedPlayer({});
                    hidePlayersModal();
                  }}>{t('SKYWORK_TOPUP_CONFIRM_SEND')}</Button>
                </Modal.Footer>
              </Modal>
              <Modal show={showOrganizerTopup} onHide={() => setShowOrganizerTopup(false)} dialogClassName="skywork-invite-modal skywork-topup-confirm-modal">
                <Modal.Header closeButton>
                  <Modal.Title>{t('INSUFFICIENT_CREDIT')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="skywork-invite-slot-summary">
                    <strong>{field.name} · {field.sport || t('SPORT')}</strong>
                    <span>{new Date(startSlot.startDatetime).toLocaleDateString(i18n.language)} · {new Date(startSlot.startDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}–{new Date(endSlot.endDatetime).toLocaleTimeString(i18n.language).slice(0, 5)}</span>
                  </div>
                  <p>{t('INSUFFICIENT_CREDIT_GUEST')}</p>
                  <p className="skywork-topup-safety-note">{t('SKYWORK_ORGANIZER_TOPUP_RESUME', {amount: organizerTopupAmount.toFixed(2)})}</p>
                </Modal.Body>
                <Modal.Footer className="skywork-invite-footer">
                  <Button type="button" variant="outline-primary" onClick={() => setShowOrganizerTopup(false)}>{t('CANCEL')}</Button>
                  <Button type="button" className="skywork-primary-action" onClick={() => startGuestTopUp(values, organizerTopupAmount, startSlot.slotId, recoveryContext)}>{t('SKYWORK_TOPUP_AND_CONTINUE')}</Button>
                </Modal.Footer>
              </Modal>
            </Form>
          );
        }}
      </Formik>
      <BookingGuideModal show={showBookingGuide} onHide={() => setShowBookingGuide(false)} context={startSlot.minimumPlayers !== null ? 'invitation' : 'booking'}/>
      {confirmationValues ? (
      <section
        ref={confirmationRef}
        className="skywork-booking-confirm-modal skywork-booking-confirm-inline"
        role="dialog"
        aria-modal="false"
        aria-labelledby="booking-confirmation-title"
        tabIndex="-1"
      >
        <Modal.Header>
          <div className="skywork-booking-confirm-heading">
            <span><FaCheckCircle aria-hidden="true"/></span>
            <div>
              <small>{bookingTypeLabel}</small>
              <Modal.Title id="booking-confirmation-title">{t('CONFIRM_RESERVATIONS')}</Modal.Title>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="skywork-booking-confirm-court">
            <FaMapMarkerAlt aria-hidden="true"/>
            <div>
              <strong>{field.name}</strong>
              <small>{partner.name}{sportLabel ? ` · ${sportLabel}` : ''}</small>
            </div>
          </div>
          <div className="skywork-booking-confirm-details">
            <div><FaCalendarAlt aria-hidden="true"/><span>{new Date(startSlot.startDatetime).toLocaleDateString(i18n.language)}</span></div>
            <div><FaClock aria-hidden="true"/><span>{new Date(startSlot.startDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})}–{new Date(endSlot.endDatetime).toLocaleTimeString(i18n.language, {hour: '2-digit', minute: '2-digit'})}</span></div>
            <div><FaUsers aria-hidden="true"/><span>{confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost' ? confirmationValues.pricingParticipantCount : (confirmationCosts ? confirmationCosts.peopleCount : 1)}{confirmationValues && confirmationValues.affiliationPricingMode !== 'dedicated_field_cost' ? ` / ${presetPlaces}` : ''}</span></div>
          </div>
          {confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost' && dedicatedQuote ? (
            <div className="skywork-booking-confirm-costs">
              <span><small>{t('DEDICATED_BASE_RATE')}</small><strong>{Number(dedicatedQuote.baseHourlyRate).toFixed(2)} €</strong></span>
              <span><small>{t('DEDICATED_ADDITIONAL_RATE')}</small><strong>{Number(dedicatedQuote.additionalParticipantsHourlyRate).toFixed(2)} €</strong></span>
              <span><small>{t('HOURLY_RATE')}</small><strong>{Number(dedicatedQuote.hourlyRate).toFixed(2)} €</strong></span>
              <span><small>{t('FIELD_PRICE_LABEL')}</small><strong>{Number(dedicatedQuote.fieldTotal).toFixed(2)} €</strong></span>
              <span><small>{t('FIXED_COSTS')}</small><strong>{Number(dedicatedQuote.fixedCosts).toFixed(2)} €</strong></span>
              <span className="skywork-booking-confirm-total"><FaEuroSign aria-hidden="true"/><small>{t('SKYWORK_AMOUNT_DUE_TO_BOOK')}</small><strong>{Number(dedicatedQuote.total).toFixed(2)} €</strong></span>
              {Number(dedicatedQuote.shortfall || 0) > 0 ? <em>{t('DEDICATED_FIELD_COST_TOPUP_REQUIRED', {amount: Number(dedicatedQuote.shortfall).toFixed(2)})}</em> : null}
            </div>
          ) : confirmationCosts && (field.payment || hasPlannedPrice) ? (
            <div className="skywork-booking-confirm-costs">
              <span><small>{t('SKYWORK_YOUR_SHARE')}</small><strong>{(confirmationCosts.organizerOwn - confirmationCosts.fixedShare).toFixed(2)} €</strong></span>
              <span><small>{t('SKYWORK_YOUR_FIXED_COSTS')}</small><strong>{confirmationCosts.fixedShare.toFixed(2)} €</strong></span>
              {confirmationCosts.invitedGuestsTotal > 0 ? <span><small>{t('SKYWORK_GUESTS_PAID_BY_YOU')}</small><strong>{confirmationCosts.invitedGuestsTotal.toFixed(2)} €</strong></span> : null}
              {confirmationCosts.unassignedCount > 0 ? <span><small>{t('SKYWORK_UNASSIGNED_SHARES_PAID_BY_YOU', {count: confirmationCosts.unassignedCount})}</small><strong>{confirmationCosts.unassignedTotal.toFixed(2)} €</strong></span> : null}
              <span className="skywork-booking-confirm-total"><FaEuroSign aria-hidden="true"/><small>{field.payment ? t('SKYWORK_AMOUNT_DUE_TO_BOOK') : t('SKYWORK_PAY_AT_CLUB_TOTAL')}</small><strong>{confirmationCosts.organizerTotal.toFixed(2)} €</strong></span>
            </div>
          ) : null}
          {confirmationCosts && confirmationCosts.unassignedCount > 0 && confirmationValues && confirmationValues.affiliationPricingMode !== 'dedicated_field_cost' ? (
            <div className="skywork-booking-unassigned-notice">
              <strong>{t('SKYWORK_BOOK_NOW_WITH_UNASSIGNED_TITLE')}</strong>
              <p>{t('SKYWORK_BOOK_NOW_WITH_UNASSIGNED_BODY', {count: confirmationCosts.unassignedCount})}</p>
            </div>
          ) : null}
          <p className="skywork-booking-confirm-payment">{confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost' ? t('DEDICATED_FIELD_COST_CHARGED_TO_AFFILIATE') : field.payment ? t('MAKING_RESERVATION_PAID') : t('MAKING_RESERVATION_TO_PAY')}</p>
          {field.bookingPolicy ? <div className="skywork-booking-confirm-policy">{t('BOOKING_POLICY', {bookingPolicy: field.bookingPolicy})}</div> : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="outline-primary" onClick={closeConfirmation} disabled={confirmationSaving}>{t('CANCEL')}</Button>
          <UnassignedInvitationActions
            visible={Boolean(confirmationCosts && confirmationCosts.unassignedCount > 0)}
            disabled={confirmationSaving}
            onInviteType={setPendingInviteType}
            onResetPlayer={() => { setInsufficientCreditPlayer(null); setSelectedPlayer({}); }}
            onCloseConfirmation={() => setConfirmationValues(null)}
            onShowPlayers={showPlayersModal}
            participantLabel={t('SKYWORK_BOOKING_ADD_PARTICIPANT')}
            guestLabel={t('SKYWORK_ADD_GUEST')}
          />
          <Button type="button" className="skywork-primary-action" onClick={confirmBooking} disabled={confirmationSaving || (confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost' && (!dedicatedQuote || dedicatedQuoteLoading))}>
            {confirmationSaving ? t('LOADING_DATA_IN_PROGRESS') : confirmationValues && confirmationValues.affiliationPricingMode === 'dedicated_field_cost' && dedicatedQuote && Number(dedicatedQuote.shortfall || 0) > 0 ? t('SKYWORK_TOPUP_AND_CONTINUE') : confirmationCosts && confirmationCosts.unassignedCount > 0 ? t('SKYWORK_BOOK_AND_PAY_ALL_SHARES') : t('CONFIRM')}
          </Button>
        </Modal.Footer>
      </section>
      ) : null}
      <PlayerBookingEditModal 
        selectedPlayer={selectedPlayer}
        setSelectedPlayer={setSelectedPlayer}
        startSlot={startSlot}
        presetPlaces={presetPlaces}
        renderPlayer={renderPlayer}
        partner={partner}
        renderPlayerSearch={renderPlayerSearch}
        booking={booking}
      />
      <Dialog
        ref={el => {
          dialog = el;
        }}/>
    </Fragment>
  );
};

PlayerBooking.propTypes = {
  booking: PropTypes.object,
  slot: PropTypes.object,
  field: PropTypes.object,
  partner: PropTypes.object,
  profile: PropTypes.object,
  save: PropTypes.func.isRequired,
  startGuestTopUp: PropTypes.func,
  startBookingTopUp: PropTypes.func,
  dedicatedAffiliations: PropTypes.array,
  token: PropTypes.string,
  slots: PropTypes.array,
  subscriptions: PropTypes.array,
  isPlayersModalVisible: PropTypes.bool
};

PlayerBooking.defaultProps = {
  booking: null,
  slot: null,
  field: null,
  partner: null,
  profile: null,
  slots: null,
  subscriptions: [],
  isPlayersModalVisible: false,
  clearPlayers: () => false,
  startGuestTopUp: () => false,
  startBookingTopUp: () => false,
  dedicatedAffiliations: [],
  token: null
};

const mapStateToProps = ({login: {profile, token}, player: {subscriptions, isPlayersModalVisible, isEditPlayersModalVisible}}) => ({
  profile,
  token,
  subscriptions,
  isPlayersModalVisible,
  isEditPlayersModalVisible
});

const mapDispatchToProps = dispatch => ({
  showPlayersModal: () => {
    dispatch(showPlayersModal());
  },
  hidePlayersModal: () => {
    dispatch(hidePlayersModal());
    dispatch(cleanPlayersReceived());
  },
  searchPlayers: (playerNickname, playerName, playerSurname, playerAffiliate, slotId, partnerId, playersNumber, matchFormat) => {
    if (playerAffiliate === undefined) playerAffiliate = false;
    if (playerName) playerName = playerName.replace(/ /g,'_');
    if (playerSurname) playerSurname = playerSurname.replace(/ /g,'_');
    if (playerNickname) playerNickname = playerNickname.replace(/ /g,'_');
    const validRegex = /^[A-Za-z0-9_àèéìòù'-]*$/;
    if ((playerName && !playerName.match(validRegex)) || (playerSurname && !playerSurname.match(validRegex)) || (playerNickname && !playerNickname.match(validRegex))) {
      dispatch(showMessage('ACCEPTED_CHARACTERS', "ACCEPTED_CHARACTERS_INFO"));
      return Promise.resolve({status: 400, error: 'invalid_characters'});
    }
    return dispatch(searchPlayers(playerNickname, playerName, playerSurname, playerAffiliate, slotId, partnerId, playersNumber, matchFormat));
  },
  clearPlayers: () => dispatch(cleanPlayersReceived()),
  showMessage: (title, body) => {
    dispatch(showMessage(title,body));
  },
  showEditPlayersModal: () => {
    dispatch(showEditPlayersModal());
  },
  hideEditPlayersModal: () => {
    dispatch(hideEditPlayersModal());
  },
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(PlayerBooking));
